import React, { createContext, useContext } from 'react';
import { useSettings } from './SettingsContext';
import i18n from '../../i18n';

const th = (key, opts) => i18n.t(`serviceMessages.theme.${key}`, opts);

// Create context
const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error(th('hook_outside_provider'));
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const { settings, loading } = useSettings();
  
  // If settings are still loading, provide default theme values
  if (loading || !settings) {
    const defaultTheme = {
      colors: {
        primary: '#3b82f6',
        primaryLight: '#60a5fa',
        primaryDark: '#2563eb',
        secondary: '#10b981',
        secondaryLight: '#34d399',
        secondaryDark: '#059669',
      },
      fonts: {
        primary: 'Inter',
        size: 'medium'
      }
    };
    
    return (
      <ThemeContext.Provider value={defaultTheme}>
        {children}
      </ThemeContext.Provider>
    );
  }
  
  // Get colors from settings or use defaults
  const primaryColor = settings?.design?.primaryColor || '#3b82f6';
  const secondaryColor = settings?.design?.secondaryColor || '#10b981';
  
  // Primary color variations
  const primaryLight = adjustColor(primaryColor, 30);
  const primaryDark = adjustColor(primaryColor, -30);
  
  // Secondary color variations
  const secondaryLight = adjustColor(secondaryColor, 30);
  const secondaryDark = adjustColor(secondaryColor, -30);
  
  // Font settings
  const primaryFont = settings?.design?.primaryFont || 'Inter';
  const fontSize = settings?.design?.fontSize || 'medium';

  // Theme object
  const theme = {
    colors: {
      primary: primaryColor,
      primaryLight,
      primaryDark,
      secondary: secondaryColor,
      secondaryLight,
      secondaryDark,
    },
    fonts: {
      primary: primaryFont,
      size: fontSize
    }
  };
  
  return (
    <ThemeContext.Provider value={theme}>
      {children}
    </ThemeContext.Provider>
  );
};

// Helper function to lighten/darken colors
function adjustColor(color, percent) {
  if (!color || color.length < 7) return color;
  
  try {
    // Convert hex to RGB
    let r = parseInt(color.substring(1, 3), 16);
    let g = parseInt(color.substring(3, 5), 16);
    let b = parseInt(color.substring(5, 7), 16);
    
    // Adjust colors
    r = Math.min(255, Math.max(0, r + Math.floor(256 * percent / 100)));
    g = Math.min(255, Math.max(0, g + Math.floor(256 * percent / 100)));
    b = Math.min(255, Math.max(0, b + Math.floor(256 * percent / 100)));
    
    // Convert back to hex
    return '#' + r.toString(16).padStart(2, '0') + 
                g.toString(16).padStart(2, '0') + 
                b.toString(16).padStart(2, '0');
  } catch (e) {
    console.error('Error adjusting color:', e);
    return color;
  }
}
